import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import ModalOverlay from '../components/shared/ModalOverlay';

const AppDialogContext = createContext(null);

/**
 * @typedef {{
 *   title?: string,
 *   message: string,
 *   confirmLabel?: string,
 *   cancelLabel?: string,
 *   variant?: 'danger' | 'primary',
 * }} ConfirmOptions
 *
 * @typedef {{
 *   title?: string,
 *   message: string,
 *   confirmLabel?: string,
 * }} AlertOptions
 */

export function AppDialogProvider({ children }) {
  const [dialog, setDialog] = useState(null);
  const resolveRef = useRef(null);

  const closeWith = useCallback((value) => {
    const resolve = resolveRef.current;
    resolveRef.current = null;
    setDialog(null);
    resolve?.(value);
  }, []);

  const confirm = useCallback((options) => {
    const opts = typeof options === 'string' ? { message: options } : options || {};
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setDialog({
        type: 'confirm',
        title: opts.title || 'Confirm',
        message: opts.message || '',
        confirmLabel: opts.confirmLabel || 'Confirm',
        cancelLabel: opts.cancelLabel || 'Cancel',
        variant: opts.variant || 'danger',
      });
    });
  }, []);

  const alert = useCallback((options) => {
    const opts = typeof options === 'string' ? { message: options } : options || {};
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setDialog({
        type: 'alert',
        title: opts.title || 'Notice',
        message: opts.message || '',
        confirmLabel: opts.confirmLabel || 'OK',
      });
    });
  }, []);

  const value = useMemo(() => ({ confirm, alert }), [confirm, alert]);

  const confirmBtnClass =
    dialog?.variant === 'primary'
      ? 'bg-primary-600 text-white hover:bg-primary-700'
      : 'bg-red-600 text-white hover:bg-red-700';

  return (
    <AppDialogContext.Provider value={value}>
      {children}
      <ModalOverlay open={Boolean(dialog)} onClose={() => closeWith(dialog?.type === 'confirm' ? false : undefined)} zIndex={80}>
        {dialog ? (
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="app-dialog-title"
            className="relative w-full max-w-lg rounded-xl border border-gray-200 bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border-b border-gray-200 px-6 py-4">
              <h2 id="app-dialog-title" className="text-lg font-semibold text-gray-900">
                {dialog.title}
              </h2>
            </div>
            <div className="px-6 py-5">
              <div className="whitespace-pre-line text-sm text-gray-700">{dialog.message}</div>
            </div>
            <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4">
              {dialog.type === 'confirm' ? (
                <>
                  <button
                    type="button"
                    onClick={() => closeWith(false)}
                    className="rounded-lg border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50"
                  >
                    {dialog.cancelLabel}
                  </button>
                  <button
                    type="button"
                    onClick={() => closeWith(true)}
                    className={`rounded-lg px-4 py-2 ${confirmBtnClass}`}
                  >
                    {dialog.confirmLabel}
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => closeWith(undefined)}
                  className="rounded-lg bg-primary-600 px-4 py-2 text-white hover:bg-primary-700"
                >
                  {dialog.confirmLabel}
                </button>
              )}
            </div>
          </div>
        ) : null}
      </ModalOverlay>
    </AppDialogContext.Provider>
  );
}

export function useAppDialog() {
  const ctx = useContext(AppDialogContext);
  if (!ctx) {
    throw new Error('useAppDialog must be used within AppDialogProvider');
  }
  return ctx;
}
